// @flow
import getConfig from 'next/config'
import jwtDecode from 'jwt-decode'

const { API_IPBX, API_LOGGING, PORTAL_URL } = getConfig().publicRuntimeConfig

export function isBrowser() {
  return typeof window !== 'undefined'
}

export function getDataFromLocalStorage(key: string, defaultValue: any) {
  if (typeof window === 'undefined') {
    return defaultValue
  }

  let value = defaultValue

  try {
    value = JSON.parse(window.localStorage.getItem(key))
  } catch (error) {
    value = window.localStorage.getItem(key)
  }

  if (!value) {
    return defaultValue
  }

  if (defaultValue !== undefined) {
    return typeof value === typeof defaultValue ? value : defaultValue
  }

  return value
}

export function saveToLocalStorage(key: string, value: string) {
  if (typeof window === 'undefined') {
    return
  }

  window.localStorage.setItem(key, value)
}

export function getAuthToken(): string {
  if (typeof window === 'undefined') {
    return ''
  }
  return window.localStorage.anvilToken || ''
}

export function decodeAuthToken(): { [key: string]: any } {
  const authToken = getAuthToken()

  if (!authToken) {
    return {}
  }

  const decodedToken = jwtDecode(authToken)
  if (!decodedToken) {
    return {}
  }

  return decodedToken
}

export function isAuthTokenValid(): boolean {
  const decodedToken = decodeAuthToken()

  if (!decodedToken.exp) {
    return false
  }

  return decodedToken.exp * 1000 > Date.now()
}

export function getIpbxApiUrl(): string {
  return (
    (typeof window !== 'undefined' && window.localStorage.GRAPHQL_SERVER) || API_IPBX || 'https://ipbxapi-dev.voipex.io'
  )
}

export function getLoggingApiUrl(): string {
  return (
    (typeof window !== 'undefined' && window.localStorage.API_LOGGING) ||
    API_LOGGING ||
    'https://logging-api-dev.voipex.io'
  )
}

export function getPortalUrl(): string {
  return (
    (typeof window !== 'undefined' && window.localStorage.PORTAL_URL) || PORTAL_URL || 'https://portal-dev.voipex.io'
  )
}

export function convertBooleans(value: any): boolean {
  switch (value) {
    case '0':
    case 'false':
    case 'undefined':
    case 'null':
      return false
    default:
      return !!value
  }
}

export function checkIframe() {
  if (typeof window === 'undefined') {
    return false
  }

  try {
    // Browsers can block access to window.top due to same origin policy
    return window.self !== window.top
  } catch (e) {
    return true
  }
}

export function sendIframeMessage(type: string, payload: {}) {
  if (!checkIframe()) {
    return console.error('[iframe] Module is not running in iframe')
  }

  if (!type) {
    return console.error('[iframe] Iframe message should contains type property')
  }

  try {
    window.parent.postMessage(JSON.stringify({ type, payload }), '*')
  } catch (err) {
    console.error(err)
  }
}

export function queryBuilder(url: string, queryObject: {} = {}) {
  const queries = Object.entries(queryObject).reduce((acc, [query, value]) => {
    if (!value) {
      return acc
    }

    acc.push(`${window.encodeURIComponent(query)}=${window.encodeURIComponent(value)}`)
    return acc
  }, [])

  if (!queries.length) return url || '/'
  return `${url}?${queries.join('&')}`
}

export function isValidHTMLTag(tagName: string): boolean {
  if (typeof window === 'undefined') {
    return false
  }
  return !window.document
    .createElement(tagName.toUpperCase())
    .toString()
    .includes('HTMLUnknownElement')
}

export function recursivelyGetObjectValue(obj: { [key: string]: any }, path: string[]): any {
  const part = path.shift()
  let value = null

  if (part) {
    // Check if we are indexing an array
    const [arrayMatch, key, index] = part.match(/(.*)\[(\d*)\]$/) || []

    if (arrayMatch) {
      value = index ? obj && obj[key] && obj[key][index] : obj && obj[key]
    } else {
      value = obj && obj[part]
    }

    return typeof value === 'object' && !Array.isArray(value) ? recursivelyGetObjectValue(value, path) : value
  }

  return value
}

export function normalize(pattern: string) {
  return String(pattern)
    .toLowerCase()
    .replace(/\s/g, '')
}

export function fulltext(searchKeys: string[] = [], pattern: string) {
  return (objectItem: { [key: string]: any }): boolean => {
    if (!pattern) {
      return true
    }

    return searchKeys.some(key => {
      const keyPath = key.split('.')
      const value = recursivelyGetObjectValue(objectItem, keyPath)

      if (Array.isArray(value)) {
        return value.some(arrayElement => normalize(arrayElement).includes(normalize(pattern)))
      }

      return normalize(value).includes(normalize(pattern))
    })
  }
}

export function getDayAbbreviation(weekDay: number, msg: (path: string) => string): string {
  const weekDays = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday']
  const selectedDay = weekDays[weekDay - 1]

  if (selectedDay === undefined) {
    return ''
  }

  return msg(`common.dayAbbreviations.${selectedDay}`)
}

export function isNullOrUndefined(value: any) {
  return value === undefined || value === null
}

export function isEmptyObject(object: { [key: string]: any }) {
  if (typeof object !== 'object' || Array.isArray(object)) {
    return false
  }
  return Object.keys(object).length === 0
}

export function isEmptyArray(array: any[]) {
  if (!Array.isArray(array)) {
    return false
  }

  return array.length === 0
}

export function filterNullAndUndefined(data: { [key: string]: any }) {
  return Object.keys(data).reduce((acc, key) => {
    const value = data[key]
    if (!isNullOrUndefined(value)) {
      acc[key] = value
    }

    return acc
  }, {})
}

export function csv2json(csvText: string) {
  const csvTextWithoutEndNewLines = csvText.replace(/(\n|\r|\t)+$/gm, '')
  const [header, ...lines] = csvTextWithoutEndNewLines.split('\n')
  const headerValues = header.split(',')

  return lines.map<{ [key: string]: string }>(line => {
    const lineValues = line.split(',')
    return headerValues.reduce((acc, header, idx) => {
      acc[header] = lineValues[idx] || ''
      return acc
    }, {})
  })
}

export function withoutPropagation(handler: (SyntheticEvent<HTMLElement>) => any) {
  return (event: SyntheticEvent<HTMLElement>) => {
    event.stopPropagation()
    handler(event)
  }
}

export function truncate(input: string = '', maxLength: number = 100): string {
  if (input.length > maxLength) {
    return `${input.substring(0, maxLength)}...`
  }
  return input
}

export function readFile(file: File, base64?: boolean = false): Promise<string> {
  return new Promise((resolve, reject) => {
    const fileReader = new window.FileReader()

    fileReader.addEventListener('load', () => resolve(fileReader.result))
    fileReader.addEventListener('error', error => reject(error))

    if (base64) {
      fileReader.readAsDataURL(file)
    } else {
      fileReader.readAsText(file)
    }
  })
}

export function reorder(arr: any[], from: number, to: number) {
  return arr.reduce((acc, current, idx, self) => {
    if (from === to) {
      acc.push(current)
    }
    if (idx === from) {
      return acc
    }
    if (from < to) {
      acc.push(current)
    }
    if (idx === to) {
      acc.push(self[from])
    }
    if (from > to) {
      acc.push(current)
    }
    return acc
  }, [])
}
